import { Fragment, useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, brl, formatDate } from "../lib/api";
import { useUser } from "../hooks/useUser";

type Activation = {
  id: number;
  featureKey: string;
  status: string;
  expiresAt: string | null;
  stripeSubscriptionId: string | null;
  stripePaymentIntentId: string | null;
  activatedAt: string;
  deactivatedAt: string | null;
  deactivationReason: string | null;
};

type PaymentIntent = {
  id: number;
  stripePaymentIntentId: string;
  amount: number;
  currency: string | null;
  status: string;
  productKey: string | null;
  createdAt: string;
  refundedAmount: number;
};

type SubscriptionItem = {
  stripeItemId: string;
  stripePriceId: string;
  featureKey: string;
  quantity: number;
  status: string;
  addedAt: string;
  removedAt: string | null;
};

type Subscription = {
  id: number;
  stripeSubscriptionId: string;
  status: string;
  currentPeriodEnd: string | null;
  canceledAt: string | null;
  createdAt: string;
  items: SubscriptionItem[];
};

type CatalogProduct = {
  id: string;
  name: string;
  description: string;
  feature_key: string;
  amount_cents: number;
  currency: string;
  mode: "one-time" | "subscription";
  checkout_mode: "inline" | "hosted";
  recurring_interval?: "day" | "week" | "month" | "year";
  seatable?: boolean;
};

type AddonState =
  | { kind: "idle" }
  | { kind: "running"; subId: string; featureKey: string }
  | { kind: "error"; subId: string; featureKey: string; message: string };

function StatusPill({ status }: { status: string }) {
  return <span className={`pill pill-${status}`}>{status}</span>;
}

export function MyAccount() {
  const { user } = useUser();
  const [activations, setActivations] = useState<Activation[]>([]);
  const [payments, setPayments] = useState<PaymentIntent[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [refundingId, setRefundingId] = useState<string | null>(null);
  /** PIs pra quais já disparamos POST /api/refunds mas o webhook ainda
   *  não atualizou refundedAmount no DB. Local-only — limpa quando o servidor
   *  confirma que refundedAmount > 0. */
  const [pendingRefunds, setPendingRefunds] = useState<Set<string>>(new Set());
  const [catalog, setCatalog] = useState<CatalogProduct[]>([]);
  const [addonOpenSubId, setAddonOpenSubId] = useState<string | null>(null);
  const [addonState, setAddonState] = useState<AddonState>({ kind: "idle" });
  // Quantidade escolhida por addon seatable (keyed por feature_key). Default 1.
  const [addonQty, setAddonQty] = useState<Record<string, number>>({});

  const refresh = useCallback(async () => {
    if (!user) return;
    const [a, p, s] = await Promise.all([
      api<{ data: Activation[] }>(`/api/users/${user.id}/activations`),
      api<{ data: PaymentIntent[] }>(`/api/users/${user.id}/payment-intents`),
      api<{ data: Subscription[] }>(`/api/users/${user.id}/subscriptions`),
    ]);
    if (a.ok) setActivations(a.body.data);
    if (p.ok) {
      const fresh = p.body.data;
      setPayments(fresh);
      // Limpa pending pra PIs onde o servidor já reflete o refund
      setPendingRefunds((prev) => {
        const next = new Set(prev);
        for (const pi of fresh) {
          if (pi.refundedAmount > 0) next.delete(pi.stripePaymentIntentId);
        }
        return next;
      });
    }
    if (s.ok) setSubscriptions(s.body.data);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  // Catalog carrega só uma vez — usado pra mostrar add-ons disponíveis.
  useEffect(() => {
    api<{ data: CatalogProduct[] }>("/api/catalog").then((r) => {
      if (r.ok) setCatalog(r.body.data);
    });
  }, []);

  const addAddon = async (
    sub: Subscription,
    featureKey: string,
    quantity = 1
  ) => {
    setAddonState({ kind: "running", subId: sub.stripeSubscriptionId, featureKey });
    const r = await api<{ subscription_id: string; status: string }>(
      "/api/subscription-items",
      {
        method: "POST",
        body: JSON.stringify({
          user_id: user!.id,
          subscription_id: sub.stripeSubscriptionId,
          feature_key: featureKey,
          quantity,
        }),
      }
    );
    if (!r.ok) {
      setAddonState({
        kind: "error",
        subId: sub.stripeSubscriptionId,
        featureKey,
        message:
          (r.body as any)?.message ??
          (r.body as any)?.error ??
          `Falha (HTTP ${r.status})`,
      });
      return;
    }
    setAddonState({ kind: "idle" });
    setAddonOpenSubId(null);
    refresh();
  };

  if (!user) return <Navigate to="/login" replace />;

  const openPortal = async () => {
    if (!user.stripeCustomerId) return;
    setPortalLoading(true);
    const r = await api<{ url: string }>("/api/customer-portal-sessions", {
      method: "POST",
      body: JSON.stringify({
        customer: user.stripeCustomerId,
        return_url: window.location.href,
      }),
    });
    setPortalLoading(false);
    if (r.ok && r.body?.url) window.location.href = r.body.url;
  };

  const cancelSub = async (sub: Subscription) => {
    const itemList = sub.items
      .filter((i) => i.status === "active")
      .map((i) => i.featureKey)
      .join(", ");
    if (
      !confirm(
        `Cancelar assinatura ${sub.stripeSubscriptionId} (${itemList || "sem items ativos"}) no fim do período atual?`
      )
    )
      return;
    setCancellingId(sub.stripeSubscriptionId);
    await api(`/api/subscriptions/${sub.stripeSubscriptionId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ at_period_end: true }),
    });
    setCancellingId(null);
    refresh();
  };

  const removeItem = async (sub: Subscription, item: SubscriptionItem) => {
    const decrement = item.quantity > 1;
    const msg = decrement
      ? `Remover 1 de ${item.featureKey}? (${item.quantity} → ${item.quantity - 1})\n\nCrédito prorated na próxima fatura.`
      : `Remover ${item.featureKey} da assinatura ${sub.stripeSubscriptionId}?\n\nCrédito prorated na próxima fatura. A feature será desativada quando o webhook chegar.`;
    if (!confirm(msg)) return;
    setRemovingItemId(item.stripeItemId);
    const r = await api<{ remaining_items: number }>(
      `/api/subscriptions/${sub.stripeSubscriptionId}/items/${item.stripeItemId}`,
      { method: "DELETE" }
    );
    setRemovingItemId(null);
    if (!r.ok) {
      alert(
        `Falhou ao remover (HTTP ${r.status}): ${
          (r.body as any)?.message ?? (r.body as any)?.error ?? "erro desconhecido"
        }`
      );
    }
    refresh();
  };

  const refundPayment = async (p: PaymentIntent) => {
    const amountStr = (p.amount / 100).toLocaleString("pt-BR", {
      style: "currency",
      currency: (p.currency ?? "brl").toUpperCase(),
    });
    if (
      !confirm(
        `Reembolsar ${amountStr} do pagamento ${p.stripePaymentIntentId}?\n\nA feature associada será marcada como 'refunded' após o webhook chegar.`
      )
    )
      return;
    setRefundingId(p.stripePaymentIntentId);
    const r = await api<{ id: string; status: string }>("/api/refunds", {
      method: "POST",
      body: JSON.stringify({
        payment_intent: p.stripePaymentIntentId,
        reason: "requested_by_customer",
        metadata: { source: "my-account" },
      }),
    });
    setRefundingId(null);
    if (r.ok) {
      setPendingRefunds((prev) => new Set(prev).add(p.stripePaymentIntentId));
    } else {
      alert(`Falhou ao reembolsar (HTTP ${r.status}): ${JSON.stringify(r.body)}`);
    }
    refresh();
  };

  return (
    <div className="my-account">
      <header className="page-header">
        <h1>Minha Conta</h1>
        <div className="account-meta">
          <span>
            <strong>{user.name ?? user.email}</strong> · #{user.id} ·{" "}
            <code>{user.stripeCustomerId ?? "sem mapping"}</code>
          </span>
          <button
            onClick={openPortal}
            disabled={!user.stripeCustomerId || portalLoading}
            className="ghost-btn"
          >
            {portalLoading ? "..." : "Customer Portal →"}
          </button>
        </div>
      </header>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <>
          <section>
            <h2>Features Ativas</h2>
            {activations.length === 0 ? (
              <p className="empty">Nenhuma feature ainda. Vá pra Loja e compre algo.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Status</th>
                    <th>Ativada em</th>
                    <th>Expira</th>
                    <th>Origem</th>
                  </tr>
                </thead>
                <tbody>
                  {activations.map((a) => (
                    <tr key={a.id}>
                      <td><code>{a.featureKey}</code></td>
                      <td><StatusPill status={a.status} /></td>
                      <td>{formatDate(a.activatedAt)}</td>
                      <td>{formatDate(a.expiresAt)}</td>
                      <td>
                        {a.stripeSubscriptionId ? (
                          <code className="muted">sub</code>
                        ) : a.stripePaymentIntentId ? (
                          <code className="muted">one-time</code>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h2>Assinaturas</h2>
            {subscriptions.length === 0 ? (
              <p className="empty">Nenhuma assinatura.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Sub / Item</th>
                    <th>Status</th>
                    <th>Próx. cobrança</th>
                    <th>ID Stripe</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((s) => {
                    const activeItems = s.items.filter((i) => i.status === "active");
                    const subActiveFeatureKeys = new Set(
                      activeItems.map((i) => i.featureKey)
                    );
                    // Intervalo da sub = intervalo dos items ativos (compartilham
                    // o ciclo). O Stripe ATÉ aceita misturar mensal+anual numa
                    // sub, mas a cobrança de intervalos misturados fica confusa —
                    // então só oferecemos add-ons do MESMO intervalo.
                    const subInterval =
                      activeItems
                        .map(
                          (i) =>
                            catalog.find((p) => p.feature_key === i.featureKey)
                              ?.recurring_interval
                        )
                        .find(Boolean) ?? null;
                    // Add-ons elegíveis: feature_catalog mode=sub, mesmo intervalo
                    // da sub, não já presente como item ativo desta sub.
                    const eligibleAddons = catalog.filter(
                      (p) =>
                        p.mode === "subscription" &&
                        (!subInterval || p.recurring_interval === subInterval) &&
                        // seatable continua elegível mesmo já presente: permite
                        // somar assentos. Não-seatable some após ser adicionado.
                        (p.seatable || !subActiveFeatureKeys.has(p.feature_key))
                    );
                    const isOpen = addonOpenSubId === s.stripeSubscriptionId;
                    const addonError =
                      addonState.kind === "error" &&
                      addonState.subId === s.stripeSubscriptionId
                        ? addonState.message
                        : null;
                    const canRemoveItem = activeItems.length > 1;
                    return (
                      <Fragment key={s.id}>
                        <tr>
                          <td>
                            <strong>Assinatura #{s.id}</strong>
                            <div className="muted small">
                              {activeItems.length} item{activeItems.length === 1 ? "" : "s"} ativo{activeItems.length === 1 ? "" : "s"}
                            </div>
                          </td>
                          <td><StatusPill status={s.status} /></td>
                          <td>{formatDate(s.currentPeriodEnd)}</td>
                          <td><code className="muted">{s.stripeSubscriptionId}</code></td>
                          <td>
                            {s.status === "active" && (
                              <>
                                {eligibleAddons.length > 0 && (
                                  <button
                                    onClick={() =>
                                      setAddonOpenSubId(isOpen ? null : s.stripeSubscriptionId)
                                    }
                                    className="ghost-btn small"
                                  >
                                    {isOpen ? "Fechar" : "+ Adicionar extensão"}
                                  </button>
                                )}
                                <button
                                  onClick={() => cancelSub(s)}
                                  disabled={cancellingId === s.stripeSubscriptionId}
                                  className="ghost-btn small"
                                >
                                  {cancellingId === s.stripeSubscriptionId
                                    ? "..."
                                    : "Cancelar sub"}
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                        {s.items.map((item) => (
                          <tr key={item.stripeItemId} className="sub-item-row">
                            <td>
                              <span className="muted">└─</span>{" "}
                              <code>{item.featureKey}</code>
                              {item.quantity > 1 && (
                                <span className="muted"> ×{item.quantity}</span>
                              )}
                            </td>
                            <td><StatusPill status={item.status} /></td>
                            <td>—</td>
                            <td><code className="muted small">{item.stripeItemId}</code></td>
                            <td>
                              {(() => {
                                // Item seatable com qty>1: permite tirar 1 assento
                                // (decrement) mesmo sendo o único item. Caso geral:
                                // remover item inteiro, só com 2+ itens na sub.
                                const itemSeatable = catalog.some(
                                  (p) =>
                                    p.feature_key === item.featureKey && p.seatable
                                );
                                const canDecrement =
                                  itemSeatable && item.quantity > 1;
                                const show =
                                  s.status === "active" &&
                                  item.status === "active" &&
                                  (canDecrement || canRemoveItem);
                                if (!show) return null;
                                return (
                                  <button
                                    onClick={() => removeItem(s, item)}
                                    disabled={
                                      removingItemId === item.stripeItemId
                                    }
                                    className="ghost-btn small"
                                  >
                                    {removingItemId === item.stripeItemId
                                      ? "..."
                                      : canDecrement
                                        ? "Remover 1 assento"
                                        : "Remover item"}
                                  </button>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                        {isOpen && (
                          <tr className="row-detail">
                            <td colSpan={5}>
                              <div className="addon-picker">
                                <p className="hint">
                                  Adicionar como Subscription Item nesta sub — cobrança
                                  prorated imediata no cartão da sub.
                                </p>
                                {eligibleAddons.map((a) => {
                                  const running =
                                    addonState.kind === "running" &&
                                    addonState.subId === s.stripeSubscriptionId &&
                                    addonState.featureKey === a.feature_key;
                                  const qty = addonQty[a.feature_key] ?? 1;
                                  return (
                                    <div key={a.id} className="addon-row">
                                      <div>
                                        <strong>{a.name}</strong>
                                        <span className="muted">
                                          {" "}— {brl(a.amount_cents, a.currency)}/
                                          {a.recurring_interval ?? "?"}
                                        </span>
                                      </div>
                                      <div className="addon-actions">
                                        {a.seatable && (
                                          <input
                                            type="number"
                                            min={1}
                                            value={qty}
                                            onChange={(e) =>
                                              setAddonQty((prev) => ({
                                                ...prev,
                                                [a.feature_key]: Math.max(
                                                  1,
                                                  parseInt(e.target.value, 10) || 1
                                                ),
                                              }))
                                            }
                                            disabled={addonState.kind === "running"}
                                            title="Quantidade de assentos"
                                            style={{ width: 56 }}
                                          />
                                        )}
                                        <button
                                          onClick={() =>
                                            addAddon(
                                              s,
                                              a.feature_key,
                                              a.seatable ? qty : 1
                                            )
                                          }
                                          disabled={addonState.kind === "running"}
                                          className="primary-btn small"
                                        >
                                          {running ? "Adicionando..." : "Adicionar"}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                                {addonError && (
                                  <p className="error">{addonError}</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h2>Histórico de Pagamentos</h2>
            {payments.length === 0 ? (
              <p className="empty">Nenhum pagamento ainda.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Valor</th>
                    <th>Feature</th>
                    <th>Status</th>
                    <th>ID</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const fullyRefunded = p.refundedAmount >= p.amount;
                    const partiallyRefunded =
                      p.refundedAmount > 0 && !fullyRefunded;
                    const pending = pendingRefunds.has(p.stripePaymentIntentId);
                    const refundable =
                      p.status === "succeeded" && !fullyRefunded && !pending;

                    let effectiveStatus = p.status;
                    if (fullyRefunded) effectiveStatus = "refunded";
                    else if (partiallyRefunded) effectiveStatus = "partial_refund";

                    return (
                      <tr key={p.id}>
                        <td>{formatDate(p.createdAt)}</td>
                        <td>
                          {brl(p.amount, p.currency ?? "brl")}
                          {p.refundedAmount > 0 && (
                            <small className="muted">
                              {" "}(−{brl(p.refundedAmount, p.currency ?? "brl")})
                            </small>
                          )}
                        </td>
                        <td><code>{p.productKey ?? "—"}</code></td>
                        <td><StatusPill status={effectiveStatus} /></td>
                        <td><code className="muted">{p.stripePaymentIntentId}</code></td>
                        <td>
                          {refundable ? (
                            <button
                              onClick={() => refundPayment(p)}
                              disabled={refundingId === p.stripePaymentIntentId}
                              className="ghost-btn small"
                            >
                              {refundingId === p.stripePaymentIntentId
                                ? "..."
                                : "Reembolsar"}
                            </button>
                          ) : pending ? (
                            <span className="muted">aguardando webhook…</span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
